import { useNavigate, useLocation, Outlet } from "react-router-dom";
// @mui
import { Box, Tabs, Tab, Typography, Stack } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// utils
import { ACTION, CATEGORY, triggerGa } from "../../utils/gaUtils";
import { useNotifications } from "../../context/NotificationContext";
// components
import { NavBarVertical } from "./nav";
import NotificationDot from "../NotificationDot";
import {
  CreatorDetailsDrawer,
  SimiliarCreatorDetailsDrawer,
} from "../Discover/drawer";
import AddToWishlistModal from "../Wishlist/add-wishlist-modal";
import { useDashboardSidebarItems } from "../../hooks/useDashboardSidebarItems";
import { dashboardRoutes } from "../../routes/DashboardRoutes";
// config
import { DRAWER, NAV } from "../../config/nav";
import colors from "../../config/colors";
// redux
import { useSelector } from "../../redux/store";

const DashboardLayout = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gmvPendingStatus } = useNotifications();
  const { sidebarItems, bottomSidebarItems } = useDashboardSidebarItems();
  const activeView = useSelector((state) => state.creator.layoutView);
  const creatorDetails = useSelector((state) => state.creator.creatorDetails);
  const isCreatorDrawerOpen = !!creatorDetails && activeView === "grid";

  const currentSidebarItem = sidebarItems.find((item) =>
    location.pathname.startsWith(item.path),
  );
  const isCampaignIntelligenceRoute = location.pathname.startsWith(
    "/campaign-intelligence",
  );
  const isCampaignRoute = location.pathname.startsWith("/campaign");
  // v2 pages own their padding and scroll; the layout adds neither.
  const isFullBleedRoute = dashboardRoutes.some(
    (route) => route.fullBleed && location.pathname.startsWith(route.path),
  );
  const currentRoutes = currentSidebarItem?.routes || [];
  const isCreatorsRoute = currentSidebarItem?.path === "/creators";
  const getTabIndex = () => {
    if (currentRoutes.length === 0) return 0;
    const index = currentRoutes.findIndex((tab) =>
      location.pathname.includes(tab.path),
    );
    return index !== -1 ? index : 0;
  };

  const selectedTab = getTabIndex();

  const handleTabChange = (_, newIndex) => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments?.[0] || ""; // 'analytics'
    const secondSegment = pathSegments?.[1] || ""; // 'sales-tracker'

    const newTabPath = currentRoutes[newIndex].path;

    triggerGa({
      category: CATEGORY.NAVIGATION,
      action: ACTION.BUTTON_CLICK,
      label: newTabPath,
      label2: "",
      page: firstSegment,
      screenName: secondSegment,
    });
    navigate(`${currentSidebarItem.path}/${newTabPath}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: embedded ? "100%" : "100vh",
        minHeight: embedded ? 0 : undefined,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          transition: "width 0.3s ease-in-out",
          width: "100%",
          height: embedded ? "100%" : "100vh",
          minHeight: embedded ? 0 : undefined,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {!embedded && (
            <NavBarVertical
              sidebarItems={sidebarItems}
              bottomSidebarItems={bottomSidebarItems}
            />
          )}
          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: 0,
              // Campaign Intelligence fills the column and owns internal scroll
              // (tabs + chat). A scrollable main + wrong vh math caused a sliver
              // of outer page scroll.
              overflowY:
                isCampaignIntelligenceRoute || isFullBleedRoute
                  ? "hidden"
                  : "auto",
              height: "100%",
              display: isFullBleedRoute ? "flex" : undefined,
              flexDirection: isFullBleedRoute ? "column" : undefined,
              p: isFullBleedRoute
                ? 0
                : isCreatorsRoute
                  ? "0px 20px 0px 20px"
                  : isCampaignIntelligenceRoute
                    ? "0px 20px"
                    : isCampaignRoute
                      ? "16px"
                      : "16px 20px",
            }}
          >
            {currentSidebarItem?.header && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack spacing={"8px"}>
                  <Typography fontWeight={600} fontSize={24}>
                    {currentSidebarItem?.header}
                  </Typography>
                  <Typography color={colors.gray200} fontSize={14}>
                    {currentSidebarItem?.description}
                  </Typography>
                </Stack>
              </Box>
            )}
            {!currentSidebarItem?.areSubRoutesGroupedSidebarTabs &&
              !currentSidebarItem?.hideTabs &&
              currentRoutes?.length > 0 && (
                <Box
                  sx={{
                    borderBottom: `2px solid ${colors.gray100}`,
                    height: "48px",
                    marginTop: isCreatorsRoute ? "0px" : "16px",
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                      style: {
                        bottom: "8px",
                        background: colors.primary500,
                        height: "4px",
                      },
                    }}
                    sx={{
                      "& .MuiTab-root": {
                        fontSize: "16px",
                        fontWeight: 500,
                        minHeight: "44px",
                        padding: "12px 16px",
                        color: colors.gray200,
                        textTransform: "capitalize",
                        paddingBottom: "20px",
                        "&.Mui-selected": {
                          color: colors.primary500,
                        },
                      },
                    }}
                  >
                    {currentRoutes.map((tab) => (
                      <Tab
                        key={tab.path}
                        label={
                          <Box sx={{ position: "relative" }}>
                            <Typography fontSize={16} fontWeight={500}>
                              {tab.label}
                            </Typography>
                            {tab.showDot &&
                              gmvPendingStatus?.data?.gmvIsPending && (
                                <NotificationDot
                                  pulse={true}
                                  size={8}
                                  top={"-8px"}
                                  right={"-12px"}
                                />
                              )}
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                </Box>
              )}
            {location.pathname.startsWith("/campaigns") && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #FFE082",
                  borderRadius: "6px",
                  p: "12px 16px",
                  mt: 2,
                  mb: 1,
                }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 20, color: "#F9A825", flexShrink: 0 }}
                />
                <Typography fontSize={14} fontWeight={400} color="#795548">
                  Amazon sales tracking is temporarily unavailable. We're
                  working on a fix - updates coming soon.
                </Typography>
              </Box>
            )}
            <Outlet />
          </Box>
        </Box>
      </Box>
      <CreatorDetailsDrawer />
      <SimiliarCreatorDetailsDrawer />
      <AddToWishlistModal />
    </Box>
  );
};

export default DashboardLayout;
